/**
 * Workshop opens on a new chat (07 Workshop, Projects / Home · a new chat and
 * Creos / New chat): the greeting, the composer, the project the chat will live
 * in, and four Creos (skills and agents) with All, which opens the Creos page.
 * A card adds itself to the prompt (In your prompt; again takes it out); ⓘ
 * opens its pop-up, whose Use it does the same. A chat always lives in a
 * project, so the picker comes filled with the latest one. With no projects yet
 * it reads New project, and the first send starts one that Creo names from the
 * message.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LayoutGrid } from "lucide-react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";
import { useWorkshopQuickCreate } from "../api/useWorkshopQuickCreate";
import {
  QUICK_CREATE_FEATURED,
  addPick,
  canAddPick,
  featuredItems,
  pickId,
  quickCreateRef,
  removePick,
} from "../quickCreate";
import { CreoCard, CreoCardSkeleton } from "./QuickCreateParts";
import WorkshopProjectPicker, { usePickableProjects } from "./WorkshopProjectPicker";
import WorkshopQuickCreateDetail from "./WorkshopQuickCreateDetail";
import WorkshopStartComposer, { type WorkshopBootstrapPayload } from "./WorkshopStartComposer";
import { useWorkshopDesk } from "./workshopDesk";
import { useComposerVoice } from "../../agent/rail/useComposerVoice";

type Props = {
  /** The project the picker starts on (a project's New chat); else the latest. */
  initialProjectId?: string | null;
  /** Words waiting for the box (a Trends or References Recreate). Never sent. */
  initialText?: string;
  /** A Quick Create's Use it: its pills wait in the box. */
  initialQuickCreates?: WorkshopQuickCreateRef[];
  canWrite: boolean;
  /** Start a chat in this project (null: no project yet, M7 names a new one). */
  onStart: (projectId: string | null, payload: WorkshopBootstrapPayload) => Promise<void>;
  onStartWithFiles: (projectId: string, files: File[]) => void;
};

const NO_PICKS: WorkshopQuickCreateRef[] = [];

export default function WorkshopNewChat({
  initialProjectId = null,
  initialText = "",
  initialQuickCreates = NO_PICKS,
  canWrite,
  onStart,
  onStartWithFiles,
}: Props) {
  const desk = useWorkshopDesk();
  const isPhone = useMediaQuery("(max-width: 767px)");
  const { active, latest, isLoading } = usePickableProjects();
  const quick = useWorkshopQuickCreate(desk.brandId);
  const [text, setText] = useState(initialText);
  const voice = useComposerVoice(desk.brandId, setText);
  const [pills, setPills] = useState<WorkshopQuickCreateRef[]>(initialQuickCreates);
  const [focusSeq, setFocusSeq] = useState(initialText || initialQuickCreates.length > 0 ? 1 : 0);
  const [picked, setPicked] = useState<string | null>(initialProjectId);
  const [chosen, setChosen] = useState<WorkshopQuickCreateItem | null>(null);

  // The picked project while it is still there, else the latest by activity.
  const project = active.find((p) => p.id === picked) ?? latest;
  const firstRun = !isLoading && active.length === 0;

  // The picked project’s folder opens in the sidebar.
  const pickedId = project?.id ?? null;
  const { setPickedProjectId } = desk;
  useEffect(() => {
    setPickedProjectId(pickedId);
  }, [pickedId, setPickedProjectId]);

  /* The greeting stays where it is once the composer starts to grow (a pill,
     a second line): the column is centred, so a taller composer would push the
     title up as well as everything under it down (user report, 12 Oct). Its
     place is read while the composer is empty and held from the first
     change; emptied again, the column centres as before. */
  const rootRef = useRef<HTMLDivElement>(null);
  const greetingRef = useRef<HTMLHeadingElement>(null);
  const restTop = useRef<number | null>(null);
  const [pinTop, setPinTop] = useState<number | null>(null);
  // Anything in the box can grow it, so the place is held from the first word.
  const growing = !isPhone && (pills.length > 0 || text.length > 0);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const greeting = greetingRef.current;
    if (growing || !root || !greeting) return;
    restTop.current = greeting.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop;
  });
  useLayoutEffect(() => {
    // Measured layout, applied before paint so the greeting never jumps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!growing) setPinTop(null);
    else if (restTop.current !== null) setPinTop((current) => current ?? restTop.current);
  }, [growing]);

  if (isLoading) {
    return (
      <div className="ws-newchat ws-newchat--loading" aria-busy="true">
        <p className="ws-loading-line" role="status">
          <span className="ws-loading-dot" aria-hidden="true" />
          Loading Workshop{desk.brandName ? ` for ${desk.brandName}` : ""}
        </p>
      </div>
    );
  }

  const picker = (
    <WorkshopProjectPicker project={project} projects={active} onPick={setPicked} canCreate={canWrite} />
  );
  const items = quick.data?.items ?? [];
  const featured = featuredItems(items);
  const total = items.length;
  const inPrompt = (item: WorkshopQuickCreateItem) => pills.some((p) => pickId(p) === pickId(item));
  // A card in the prompt comes out again; one with no room left opens its pop-up, which says so.
  const togglePick = (item: WorkshopQuickCreateItem) => {
    if (inPrompt(item)) {
      setPills((prev) => removePick(prev, quickCreateRef(item)));
      return;
    }
    if (!canAddPick(pills, item)) {
      setChosen(item);
      return;
    }
    setPills((prev) => addPick(prev, item));
    setFocusSeq((n) => n + 1);
  };

  return (
    <div
      ref={rootRef}
      className="ws-newchat"
      data-first={firstRun || undefined}
      style={pinTop === null ? undefined : { justifyContent: "flex-start", paddingTop: pinTop }}
    >
      <h1 ref={greetingRef} className="ws-greeting">
        What are we making{desk.brandName ? ` for ${desk.brandName}` : ""}?
      </h1>
      {isPhone ? <div className="ws-tray">{picker}</div> : null}
      <div className="ws-compose">
        <WorkshopStartComposer
          voice={voice}
          text={text}
          onTextChange={setText}
          placeholder={isPhone ? "Ask Workshop" : "Start a chat: the brief, the tension, or the spark"}
          inputLabel="Start a chat"
          focusSeq={focusSeq}
          disabled={!canWrite}
          onStart={(payload) => onStart(firstRun ? null : project?.id ?? null, payload)}
          onAttachFiles={
            canWrite ? (files) => (project && !firstRun ? onStartWithFiles(project.id, files) : undefined) : undefined
          }
          attachDisabled={firstRun}
          quickCreates={pills}
          onQuickCreatesChange={setPills}
        />
        {isPhone ? null : (
          <div className="ws-tray">
            {picker}
            {firstRun ? (
              <p className="ws-tray-hint ws-tray-hint--wrap">
                Your first chat starts a project.
                <br />
                Creo names it from your message; rename it any time.
              </p>
            ) : null}
          </div>
        )}
        {firstRun && !isPhone ? (
          <p className="ws-firstrun">
            New here? Every chat lives in a project, with its own instructions and files for Creo to read.
          </p>
        ) : null}
      </div>
      {quick.isError || (quick.data && featured.length === 0) ? null : (
        <section className="ws-qc-row" aria-label="Creos">
          <div className="ws-qc-row-head">
            <h2 className="ws-qc-row-title">Creos</h2>
            <span className="ws-qc-row-push" />
            <button type="button" className="ws-qc-all" onClick={desk.openCreos}>
              <LayoutGrid aria-hidden="true" />
              {quick.data ? `All ${total}` : "All"}
            </button>
          </div>
          <ul className="ws-qc-features">
            {quick.data
              ? featured.map((item) => (
                  <CreoCard
                    key={pickId(item)}
                    item={item}
                    variant="row"
                    picked={inPrompt(item)}
                    disabled={!canWrite}
                    onPick={() => togglePick(item)}
                    onInfo={() => {
                      setChosen(item);
                    }}
                  />
                ))
              : Array.from({ length: QUICK_CREATE_FEATURED }, (_, i) => <CreoCardSkeleton key={i} variant="row" />)}
          </ul>
        </section>
      )}

      <WorkshopQuickCreateDetail
        item={chosen}
        onClose={() => setChosen(null)}
        projectId={project?.id ?? null}
        canWrite={canWrite}
        isPhone={isPhone}
        added={chosen ? pills.some((p) => pickId(p) === pickId(chosen)) : false}
        full={chosen ? !pills.some((p) => pickId(p) === pickId(chosen)) && !canAddPick(pills, chosen) : false}
        onUse={(item, projectId) => {
          setChosen(null);
          if (projectId) setPicked(projectId);
          setPills((prev) => addPick(prev, item));
          setFocusSeq((n) => n + 1);
        }}
      />
    </div>
  );
}
